// The ways into the Higgsfield import (F9b B5): a quiet card in New production and in Getting started (two
// sentences, the sentence for your own Claude to copy, the docs page; the connect step first when no Claude is
// connected; then the second way, Connect Higgsfield, where Filmsquare reads it itself), and a quiet studio banner
// while a staged plan waits to be arranged. Outline buttons only: the white primary stays with each screen's own
// action, and nothing here is a loud moment.

import { useEffect, useState } from "react";
import { BRAND } from "../../brand.ts";
import { CLAUDE_IMPORT_SENTENCE, importsApi, type PlanSummary } from "../../data/imports-api.ts";
import { docsUrl, IMPORT_DOCS } from "../../docs.ts";
import { currentLocale } from "../../locale.ts";
import { formatRoute } from "../../route.ts";
import { useApp } from "../app.ts";
import { CopyText, useClaudeConnected } from "../connect/CopyText.tsx";
import { Icon } from "../icons.tsx";
import { Button, LinkButton } from "../kit.tsx";
import { HiggsfieldPath } from "./HiggsfieldConnect.tsx";
import "./import.css";

const SETTINGS = formatRoute({ view: "settings", page: "claude" });

/** "Import from Higgsfield": what it does, the sentence for your Claude, the docs. */
export function ImportEntryCard({ compact = false }: { compact?: boolean }) {
  const { api, ws } = useApp();
  const connected = useClaudeConnected(api, ws);
  return (
    <section className={`k-card imp-entry${compact ? " compact" : ""}`} aria-labelledby="imp-entry-title">
      <div className="imp-entry-text">
        <p className="imp-entry-eyebrow">Already started somewhere else?</p>
        <h2 id="imp-entry-title">Import from Higgsfield</h2>
        <p className="imp-entry-sub">
          Bring your Higgsfield projects in as productions: the folders, stills and takes with their prompts,
          and your elements. Your own Claude reads Higgsfield and hands the work over, or you connect
          Higgsfield and {BRAND.name} reads it. Either way nothing is generated and no credits are used.
        </p>
      </div>
      <div className="imp-entry-do">
        {connected === false || connected === null ? (
          <p className="imp-entry-step">
            <span className="imp-entry-n">1</span>
            <span>
              {connected === false ? "Your Claude is not connected yet. " : ""}
              <a href={SETTINGS}>Connect your Claude in Settings → Claude</a>, then paste this into it:
            </span>
          </p>
        ) : (
          <p className="imp-entry-step">Paste this into your Claude:</p>
        )}
        <CopyText prose label="Sentence for your Claude" text={CLAUDE_IMPORT_SENTENCE} />
        <p className="imp-entry-foot">
          Claude sends you a link to arrange the projects here before anything comes in.{" "}
          <a href={docsUrl(IMPORT_DOCS, currentLocale())} target="_blank" rel="noopener">
            How it works
            <Icon name="external" size={11} />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </p>
        <HiggsfieldPath />
      </div>
    </section>
  );
}

/** The newest staged plan, while one waits (GET /import-plans?status=draft); null when there is none. */
export function useDraftPlan(): [PlanSummary | null, () => void] {
  const { api, ws } = useApp();
  const [plan, setPlan] = useState<PlanSummary | null>(null);
  const [tick, setTick] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick only asks for a fresh read
  useEffect(() => {
    let live = true;
    setPlan(null);
    importsApi(api, ws)
      .plans("draft")
      .then(
        (l) => live && setPlan(l[0] ?? null),
        // a server without imports, or a role that cannot see them: no banner
        () => undefined,
      );
    return () => {
      live = false;
    };
  }, [api, ws, tick]);
  return [plan, () => setTick((t) => t + 1)];
}

/** "3 Higgsfield projects ready to arrange · Arrange · Dismiss": quiet, in the studio's work register. */
export function ImportBanner() {
  const { api, ws } = useApp();
  const [plan, reload] = useDraftPlan();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!plan) return null;
  const n = plan.item_count;
  const dismiss = () => {
    setBusy(true);
    setFailed(false);
    importsApi(api, ws)
      .archivePlan(plan.id)
      .then(reload, () => setFailed(true))
      .finally(() => setBusy(false));
  };
  return (
    <aside className="imp-banner" aria-label="Import waiting">
      <span className="imp-banner-mark" aria-hidden="true">
        <Icon name="upload" size={14} />
      </span>
      <p className="imp-banner-text">
        <strong>
          {n} Higgsfield {n === 1 ? "project" : "projects"} ready to arrange
        </strong>
        <span>
          {failed
            ? "Could not dismiss it; only a producer can."
            : "Set them as episodes or productions, then import."}
        </span>
      </p>
      <LinkButton href={formatRoute({ view: "import", planId: plan.id })} size="sm">
        Arrange
      </LinkButton>
      <Button
        variant="text"
        size="sm"
        disabled={busy}
        onClick={dismiss}
        data-testid="imp-banner-dismiss"
        title="Set this import aside without bringing anything in"
      >
        Dismiss
      </Button>
    </aside>
  );
}
